import { createId, RecallJobDao } from '@rock/db-admin';
import type { RecallJob, RecallJobId, RecallJobRequest, Timestamp, UserId } from '@rock/types';

const recallJobDao = new RecallJobDao();

/** Rough cost of extracting one notice with both extractors (the bake-off's measured rate). */
export const PROCESS_COST_PER_RECALL_USD = 0.03;

/**
 * Request a paid action: write a `recall-jobs` doc as `requested`. The `runRecallJob` trigger claims and
 * runs it server-side, so the LLM keys never reach the browser (Decision 10). Returns the job id for
 * progress watching.
 */
export async function requestRecallJob(request: RecallJobRequest, requestedBy: UserId): Promise<RecallJobId> {
    const now = new Date().toISOString() as Timestamp;
    const job: RecallJob = {
        object: 'recall-job',
        schema: 1,
        id: createId<RecallJobId>(),
        request,
        requestedBy,
        status: 'requested',
        createdAt: now,
        updatedAt: now,
    };
    await recallJobDao.setRecallJob(job);
    return job.id;
}

export function isRecallJobActive(job: RecallJob): boolean {
    return job.status === 'requested' || job.status === 'running';
}

/** One line for an active job: whether it has started, what it is doing and how far it got. */
export function describeActiveRecallJob(job: RecallJob): string {
    return `${job.status === 'running' ? 'Running' : 'Queued'}: ${describeRecallJob(job)}`;
}

/** One line for a job: what it is doing and how far it got. */
export function describeRecallJob(job: RecallJob): string {
    const what = describeRequest(job.request);
    const progress = job.progress
        ? ` · ${job.progress.done}/${job.progress.total} · $${job.progress.costUsd.toFixed(2)}`
        : '';
    return `${what}${progress}`;
}

function describeRequest(request: RecallJobRequest): string {
    switch (request.kind) {
        case 'process':
            return `Process ${request.recallIds.length} recall(s)`;
        case 'process-kind':
            return `Process “${request.productKind}”`;
        case 'reclassify':
            return request.recallIds ? `Reclassify ${request.recallIds.length} recall(s)` : 'Reclassify stale recalls';
        case 'sweep':
            return request.notify ? 'Apply approved recalls' : 'Apply approved recalls (silent)';
        case 'impact-preview':
            return `Recalculate the Impact Preview of ${request.recallIds.length} recall(s)`;
    }
}
